/**
 * As falas dos lojistas — quem sorteia, e por que não dá para sortear com `$t`.
 *
 * ## O bug que isto conserta
 *
 * O `Shop.vue` sorteava a saudação assim:
 *
 * ```js
 * const falas = this.$t('shop.village.shops.rod.greetings');
 * if (Array.isArray(falas) && falas.length) { ... }
 * ```
 *
 * O `i18n.js` roda em **modo Composition** (`legacy: false`), e nesse modo o `t()` só devolve
 * **string**: pedir a ele uma chave cujo valor é um array devolve *a própria chave de volta*.
 * `Array.isArray` era sempre falso, o `if` nunca entrava, e todo lojista dizia a mesma frase de
 * reserva ("Bem-vindo à minha loja!") — as saudações aleatórias existiam no JSON e nunca chegaram
 * à tela. Quem lê array em modo Composition é o **`tm()`**; quem interpola um item dele é o `t()`
 * com o índice no caminho (`...greetings[2]`).
 *
 * ## Não repetir a fala anterior
 *
 * Sorteio uniforme com seis opções repete a frase em uma vez a cada seis — e repetição imediata é
 * exatamente o que o jogador percebe como "é sempre a mesma frase". Então guardamos o último
 * índice por caminho e sorteamos entre os **outros**. É memória de sessão, deliberadamente: não
 * vale persistir num `localStorage` o que só serve para o balão não gaguejar.
 */

/** O último índice sorteado por caminho de fala. Só cresce até o número de caminhos (dezenas). */
const ultimoIndice = new Map();

function sortear(caminho, quantas) {
  if (quantas <= 1) return 0;

  const anterior = ultimoIndice.get(caminho);
  let i = Math.floor(Math.random() * quantas);
  // Caiu na mesma de antes: escolhe uniformemente entre as `quantas - 1` restantes.
  if (i === anterior) i = (anterior + 1 + Math.floor(Math.random() * (quantas - 1))) % quantas;

  ultimoIndice.set(caminho, i);
  return i;
}

/**
 * Uma fala sorteada de `caminho`, já interpolada. String vazia se o caminho não existir — é o que
 * deixa o chamador tentar o genérico sem precisar saber se o lojista tem aquela fala.
 *
 * Aceita também um caminho que aponte para uma **string** só, e aí devolve ela: assim uma fala
 * pode virar lista (ou voltar a ser uma) no JSON sem mexer em componente nenhum.
 */
export function escolherFala(vm, caminho, params = {}) {
  const opcoes = typeof vm.$tm === 'function' ? vm.$tm(caminho) : null;

  if (Array.isArray(opcoes) && opcoes.length) {
    return vm.$t(`${caminho}[${sortear(caminho, opcoes.length)}]`, params);
  }

  // `t()` devolve a chave quando não acha nada — o que aqui significa "não tenho essa fala".
  const unica = vm.$t(caminho, params);
  return unica === caminho ? '' : unica;
}

/**
 * A fala de um lojista específico: `shop.village.shops.<tipo>.talk.<chave>`.
 *
 * Cada lojista tem a voz dele — o Velho João fala de madeira, o Mercador Místico fala em enigma —
 * e é por isso que a fala mora dentro da loja e não num `shop.talk` compartilhado. O `shop.talk`
 * continua existindo como **reserva**: se um lojista não tiver a fala daquele momento, o balão diz
 * a genérica em vez de ficar vazio.
 */
export function falaDoLojista(vm, tipo, chave, params = {}) {
  return (
    escolherFala(vm, `shop.village.shops.${tipo}.talk.${chave}`, params)
    || escolherFala(vm, `shop.talk.${chave}`, params)
  );
}

/** A saudação de quem entra na loja (ou clica no lojista de novo). */
export function saudacaoDoLojista(vm, tipo) {
  return (
    escolherFala(vm, `shop.village.shops.${tipo}.greetings`)
    || vm.$t('shop.village.defaultGreeting')
  );
}
